import type { Place } from '../../types/place'
import type { PassTier } from '../../data/placesIndex'
import { getSupabaseOrThrow } from '../supabaseClient'

export type PlaceCatalog = PassTier

export type DbPlace = {
  id: string
  mock_id: number | null
  catalog: PlaceCatalog
  slug: string
  name: string
  category: string | null
  region: string | null
  description: string | null
  lat: number | null
  lng: number | null
}

const inMemoryIdCache = new Map<string, string>()

function cacheKey(catalog: PlaceCatalog, placeId: number) {
  return `${catalog}:${placeId}`
}

function slugForMockPlace(catalog: PlaceCatalog, placeId: number) {
  return catalog === 'full' ? `place-full-${placeId}` : `place-${placeId}`
}

export async function ensurePlaceFromMock(
  place: Place,
  catalog: PlaceCatalog = 'mini',
): Promise<DbPlace> {
  const key = cacheKey(catalog, place.id)
  const cached = inMemoryIdCache.get(key)
  if (cached) {
    return {
      id: cached,
      mock_id: place.id,
      catalog,
      slug: slugForMockPlace(catalog, place.id),
      name: place.name,
      category: place.category ?? null,
      region: place.region ?? null,
      description: place.description ?? null,
      lat: place.lat ?? null,
      lng: place.lng ?? null,
    }
  }

  const supabase = getSupabaseOrThrow()
  const payload = {
    mock_id: place.id,
    catalog,
    slug: slugForMockPlace(catalog, place.id),
    name: place.name,
    category: place.category ?? null,
    region: place.region ?? null,
    description: place.description ?? null,
    lat: place.lat ?? null,
    lng: place.lng ?? null,
  }

  const { data, error } = await supabase
    .from('places')
    .upsert(payload, { onConflict: 'slug' })
    .select('id, mock_id, catalog, slug, name, category, region, description, lat, lng')
    .single()

  if (error) throw new Error(error.message)
  if (!data?.id) throw new Error('Nie udało się utworzyć/odczytać miejsca.')

  inMemoryIdCache.set(key, data.id)
  return data as DbPlace
}
